import { Text, Graphics, Container } from "pixi.js";
import { singleton } from "tsyringe";
import { PixiApp } from "../pixi-app";
import gsap from "gsap";
import { AppState } from "../app-state";
import { checkIsMobile } from "../utils/check-is-mobile";

const TITLE_FONT_SIZE = 52;
const TITLE_FONT_SIZE_SM = 37;
const SUBTITLE_FONT_SIZE = 24;
const SUBTITLE_FONT_SIZE_SM = 18;

@singleton()
export class PaintingPreludeComponent {
  private container: Container;
  private text: Text;
  private subTitle: Text;
  private textWidth = 0;
  private textHeight = 0;
  constructor(private pixiApp: PixiApp, private appState: AppState) {}
  mount() {
    this.mountContainer();
    this.mountMainTitle();
    this.mountSubTitle();
  }
  unmount() {
    this.container.parent.removeChild(this.container);
    this.container.destroy({ children: true });
    this.container = undefined;
  }
  async enter() {
    this.mount();
    await this.playEnterAnimation();
  }
  async exit() {
    await this.playExitAnimation();
    this.unmount();
  }
  playEnterAnimation() {
    return new Promise((res) => {
      const ease = "Expo.easeOut";
      const duration = 2.25;
      const tl = gsap.timeline({ defaults: { duration, ease } });
      tl.to(
        this.text,
        {
          alpha: 1,
          ease: "ease",
          duration: 2,
        },
        0.1
      );

      tl.to(
        this.subTitle,
        {
          duration: 1.25,
          y: this.subTitle.y + this.subTitle.height,
          onComplete: res,
        },
        "<+0.75"
      );
    });
  }
  playExitAnimation() {
    return new Promise((res) => {
      const ease = "ease";
      const duration = 0.7;
      const tl = gsap.timeline({ defaults: { ease, duration } });
      const offset = 0.25;
      tl.to(
        this.subTitle,
        {
          alpha: 0,
          duration: 0.5,
        },
        offset
      );
      tl.to(
        this.text,
        {
          alpha: 0,
          onComplete: res,
        },
        0
      );
    });
  }
  private mountContainer() {
    if (!this.container) {
      this.container = new Container();
    }
    this.pixiApp.stage.addChild(this.container);
  }
  private mountMainTitle() {
    const title = this.appState.activePaintingData?.title;
    const isMobile = checkIsMobile();
    const { pixiApp, container } = this;
    const text = new Text(title, {
      fontFamily: "Playfair Display",
      fontWeight: "400",
      fontSize: isMobile ? TITLE_FONT_SIZE_SM : TITLE_FONT_SIZE,
      wordWrap: true,
      align: "center",
      wordWrapWidth: document.documentElement.clientWidth,
      fill: 0xffffff,
      lineHeight: 0,
    });
    this.text = text;
    this.text.alpha = 0;
    this.textWidth = text.width;
    this.textHeight = text.height;
    text.x = pixiApp.screen.width / 2 - this.textWidth / 2;
    text.y = pixiApp.screen.height / 2 - this.textHeight / 2;
    container.addChild(text);
  }
  private mountSubTitle() {
    const title = this.appState.activePaintingData?.year;
    const isMobile = checkIsMobile();
    const { pixiApp, container } = this;
    const text = new Text(title, {
      fontFamily: "Playfair Display",
      fontWeight: "400",
      fontSize: isMobile ? SUBTITLE_FONT_SIZE_SM : SUBTITLE_FONT_SIZE,
      wordWrap: true,
      align: "center",
      wordWrapWidth: document.documentElement.clientWidth,
      fill: 0xffffff,
      lineHeight: 0,
    });
    text.x = pixiApp.screen.width / 2 - text.width / 2;
    text.y = pixiApp.screen.height / 2 + this.textHeight - 25;
    const mask = new Graphics();
    mask.beginFill(0xffffff);
    mask.drawRect(text.x, text.y, text.width, text.height);
    text.y -= text.height;
    mask.endFill();
    text.mask = mask;
    container.addChild(mask);
    container.addChild(text);
    this.subTitle = text;
  }
}
